import React from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Check, Star, Crown, Zap } from 'lucide-react';

const PricingSection = () => {
  const plans = [
    {
      name: "Basic",
      price: "₹999",
      period: "/month",
      description: "Perfect for small cafes starting their digital journey",
      icon: Zap,
      features: [
        "QR-based digital menu",
        "Basic payment integration",
        "Up to 50 menu items",
        "Basic analytics dashboard",
        "Email support",
        "1 user account"
      ],
      highlighted: false,
      cta: "Start Basic Plan"
    },
    {
      name: "Professional",
      price: "₹1,999",
      period: "/month",
      description: "Most popular plan for growing restaurants",
      icon: Star,
      features: [
        "Everything in Basic",
        "Customer CRM & loyalty program", 
        "Unlimited menu items",
        "Advanced analytics & reports",
        "WhatsApp integration",
        "Priority phone support",
        "Up to 5 user accounts",
        "Custom branding"
      ],
      highlighted: true,
      cta: "Choose Professional"
    },
    {
      name: "Enterprise",
      price: "₹4,999",
      period: "/month",
      description: "Complete solution for restaurant chains",
      icon: Crown,
      features: [
        "Everything in Professional",
        "Multi-location management",
        "Staff management system",
        "Inventory integration",
        "Advanced reporting suite",
        "24/7 dedicated support",
        "Unlimited user accounts",
        "Custom integrations",
        "Training & onboarding"
      ],
      highlighted: false,
      cta: "Contact Sales"
    }
  ];

  return (
    <section className="bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50 py-20" id="pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Simple, Transparent{' '}
            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Pricing
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Choose the perfect plan for your cafe. No hidden charges, no setup fees. Cancel anytime.
          </p>
          
          {/* Special Offer Banner */}
          <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full font-semibold mb-12">
            🎉 Limited Time: Get 2 months FREE on yearly plans!
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => {
            const Icon = plan.icon;
            return (
              <Card 
                key={plan.name} 
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-2xl ${
                  plan.highlighted 
                    ? 'border-2 border-orange-500 shadow-xl scale-105 bg-white' 
                    : 'border border-gray-200 hover:border-orange-300 bg-white'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-orange-500 to-red-500 text-white text-center py-2 text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                
                <CardHeader className={`text-center ${plan.highlighted ? 'pt-12' : 'pt-8'}`}>
                  <div className="flex justify-center mb-4">
                    <div className={`p-3 rounded-full ${
                      plan.highlighted 
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' 
                        : 'bg-orange-100 text-orange-600'
                    }`}>
                      <Icon className="h-8 w-8" />
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900">{plan.name}</h3>
                  <p className="text-gray-600 mt-2">{plan.description}</p>
                  
                  <div className="mt-6">
                    <div className="flex items-end justify-center">
                      <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                      <span className="text-lg text-gray-600 ml-1">{plan.period}</span>
                    </div>
                    <div className="text-sm text-gray-500 mt-2">
                      Billed monthly • Cancel anytime
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 px-8 pb-8">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-3">
                        <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className={`w-full py-3 font-semibold ${
                      plan.highlighted
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white'
                        : 'bg-white border-2 border-orange-500 text-orange-600 hover:bg-orange-50'
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-600 mb-6">
            Not sure which plan is right for you?
          </p>
          <Button 
            variant="outline" 
            size="lg"
            className="border-2 border-orange-500 text-orange-600 hover:bg-orange-50 px-8 py-3 font-semibold"
          >
            Schedule a Free Consultation
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;